import React from 'react';

import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {colors} from '@app/styles/theme';
import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {NotificationsGate, notificationsModel} from '@entities/notifications/model';
import {
    LocalizedTitle,
    NotificationContentDto,
    NotificationDictionaryItem,
    NotificationDto,
    NotificationMessageDto,
} from '@entities/notifications/types';
import {
    NotificationsAttachmentItem,
    NotificationsAttachmentList,
    NotificationsDateTimeCell,
    NotificationsDetailsBody,
    NotificationsDetailsDate,
    NotificationsDownloadButton,
    NotificationsDrawerHeader,
    NotificationsDrawerLoading,
    NotificationsEmptyMessage,
    NotificationsPreviewFrame,
} from '@pages/TaxpayerDetails/styled';
import {Icon} from '@shared/ui/Icon';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {Select, SelectOption} from '@shared/ui/Select';
import {Drawer} from '@shared/ui/Sidebar';
import {Spinner} from '@shared/ui/Spinner';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';
import {Title} from '@shared/ui/Title';

const localizeTitle = (title: LocalizedTitle | null | undefined, language: string) => {
    if (!title) return EMPTY;
    return title[language] ?? title.en ?? Object.values(title)[0] ?? EMPTY;
};

const config = (
    typeOptions: Array<SelectOption<string>>,
    statusOptions: Array<SelectOption<string>>,
    t: (key: string, params?: Record<string, unknown>) => string,
) => [
    {id: 'id', header: t('page_invoices.table.id'), minWidth: 173},
    {id: 'created', header: t('page_notifications.date-time'), minWidth: 151, isSortable: true},
    {
        id: 'updated',
        header: t('page_notifications.date-of-update'),
        minWidth: 157,
        isSortable: true,
    },
    {
        id: 'kind',
        header: t('page_notifications.kind'),
        minWidth: 180,
        filter: <Select isClearable options={typeOptions} />,
    },
    {id: 'name', header: t('page_notifications.violation-name'), minWidth: 240},
    {
        id: 'status',
        header: t('page_notifications.notification-status'),
        minWidth: 170,
        filter: <Select isClearable options={statusOptions} />,
    },
    {id: 'file', header: t('page_notifications.file'), width: 90},
];

const formatDateTimeCell = (value?: string | null) => {
    if (!value) return EMPTY;
    const date = new Date(value);
    return (
        <NotificationsDateTimeCell>
            <div>{format(date, 'dd.MM.yyyy')}</div>
            <div>{format(date, 'HH:mm')}</div>
        </NotificationsDateTimeCell>
    );
};

const notificationStatusColor = (code?: string | null) =>
    code === 'NEW'
        ? STATUS_COLOR_LIST.ORANGE
        : code === 'SENT'
        ? STATUS_COLOR_LIST.BLUE
        : code === 'SEND_ERROR'
        ? STATUS_COLOR_LIST.RED
        : code === 'READ'
        ? STATUS_COLOR_LIST.GREEN
        : STATUS_COLOR_LIST.GRAY;

const buildNotificationName = (
    row: NotificationDto,
    violationTypeTitle: string,
    t: (key: string, params?: Record<string, unknown>) => string,
) =>
    t('page_notifications.notification-name', {
        eventType: violationTypeTitle,
        date: row.violationCreated
            ? format(new Date(row.violationCreated), 'dd.MM.yyyy')
            : row.created
            ? format(new Date(row.created), 'dd.MM.yyyy')
            : EMPTY,
    });

const getRowPayload = (row: any) => row?.original ?? row;

const decodeBase64 = (value?: string | null) => {
    if (!value) return '';

    try {
        const binary = atob(value);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return new TextDecoder().decode(bytes);
    } catch {
        return value;
    }
};

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

const normalizeBase64DataUrl = (value: string, contentType: string) =>
    value.startsWith('data:') ? value : `data:${contentType};base64,${value}`;

const looksLikeMarkup = (value: string) =>
    /^\s*(<!doctype|<html|<body|<table|<div|<p|<style)/i.test(value);

const looksLikeBase64 = (value: string) => /^[A-Za-z0-9+/=\s]+$/.test(value);

export const ViolationNotificationsTab = () => {
    const {id} = useParams<{id: string}>();
    const {t, i18n} = useTranslation();
    const notifications = useUnit(notificationsModel);

    useGate(NotificationsGate, {violationId: id});

    const typeOptions = React.useMemo(
        () =>
            notifications.state.notificationKinds.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code || item.id,
            })),
        [i18n.language, notifications.state.notificationKinds],
    );

    const statusOptions = React.useMemo(
        () =>
            notifications.state.notificationStatuses.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code || item.id,
            })),
        [i18n.language, notifications.state.notificationStatuses],
    );

    const data = React.useMemo(
        () =>
            (notifications.state.data?.data ?? []).map((row: NotificationDto) => {
                const violationType = notifications.state.violationTypes.find(
                    (item: NotificationDictionaryItem) => item.id === row.violationType,
                );
                const status = notifications.state.notificationStatuses.find(
                    (item) => item.id === row.status || item.code === row.status,
                );
                const violationTypeTitle = localizeTitle(
                    violationType?.title ?? null,
                    i18n.language,
                );

                return {
                    ...row,
                    created: formatDateTimeCell(row.created),
                    updated: formatDateTimeCell(row.updated),
                    kind: localizeTitle(row.kind?.title ?? null, i18n.language),
                    name: buildNotificationName(row, violationTypeTitle, t),
                    status: (
                        <StatusWrapper color={notificationStatusColor(status?.code)}>
                            {localizeTitle(status?.title ?? null, i18n.language)}
                        </StatusWrapper>
                    ),
                    file: (
                        <NotificationsDownloadButton
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                notifications.notificationFileDownloadRequested(row.id);
                            }}
                            aria-label={t('actions.download')}
                        >
                            <Icon name="download" size={16} color={colors.brandPrimary} />
                        </NotificationsDownloadButton>
                    ),
                };
            }),
        [
            i18n.language,
            notifications.notificationFileDownloadRequested,
            notifications.state.data,
            notifications.state.notificationStatuses,
            notifications.state.violationTypes,
            t,
        ],
    );

    const columns = React.useMemo(
        () =>
            mapTableColumns(
                config(typeOptions, statusOptions, t),
                notifications.state.filters,
                notifications.filtersChanged,
                notifications.filtersReset,
                notifications.state.sortings,
                notifications.sortingsChanged,
            ),
        [
            notifications.filtersChanged,
            notifications.filtersReset,
            notifications.sortingsChanged,
            notifications.state.filters,
            notifications.state.sortings,
            statusOptions,
            t,
            typeOptions,
        ],
    );

    const selectedNotification = notifications.state.selectedNotification;
    const selectedMessage = notifications.state.selectedMessage;
    const selectedStatus = notifications.state.notificationStatuses.find(
        (item) =>
            item.id === selectedNotification?.status || item.code === selectedNotification?.status,
    );

    return (
        <>
            <Table
                columns={columns}
                data={data}
                isLoading={notifications.state.pending}
                pageCount={Math.ceil(
                    (notifications.state.data?.total ?? 0) / notifications.state.pageSize,
                )}
                pageIndex={notifications.state.pageNumber}
                totalRowsCount={notifications.state.data?.total ?? 0}
                onPageChange={notifications.pageNumberChanged}
                onPageSizeChange={notifications.pageSizeChanged}
                onRowClick={(row) => {
                    const payload = getRowPayload(row);
                    if (payload?.id) notifications.detailsOpened(payload.id);
                }}
            />

            <Drawer open={notifications.state.isDetailsOpen} onClose={notifications.detailsClosed}>
                <NotificationsDrawerHeader>
                    <Title size={4}>{t('page_notifications.detail-title')}</Title>
                    {selectedStatus && (
                        <StatusWrapper color={notificationStatusColor(selectedStatus.code)}>
                            {localizeTitle(selectedStatus.title, i18n.language)}
                        </StatusWrapper>
                    )}
                </NotificationsDrawerHeader>

                {notifications.state.detailsPending && !selectedMessage ? (
                    <NotificationsDrawerLoading>
                        <Spinner />
                    </NotificationsDrawerLoading>
                ) : (
                    <NotificationDetails
                        message={selectedMessage}
                        contents={notifications.state.contentList}
                        onDownloadAttachment={notifications.attachmentDownloadRequested}
                        t={t}
                    />
                )}
            </Drawer>
        </>
    );
};

export const NotificationDetails = ({
    message,
    contents,
    onDownloadAttachment,
    t,
}: {
    message: NotificationMessageDto | null;
    contents: NotificationContentDto[];
    onDownloadAttachment: (id: string) => void;
    t: (key: string, params?: Record<string, unknown>) => string;
}) => {
    if (!message) return <NotificationsEmptyMessage>{t('no_data')}</NotificationsEmptyMessage>;

    const contentTypeCode = message.bodyContentType?.code?.toUpperCase();
    const mediaType = message.bodyContentType?.mediaType || 'text/html';
    const attachments = contents.length ? contents : message.attachments?.data ?? [];
    const decodedBodyBin = decodeBase64(message.bodyBin);
    const textBody = message.body || decodedBodyBin;
    const canRenderAsSrcDoc =
        Boolean(textBody) && (contentTypeCode !== 'PDF' || looksLikeMarkup(textBody));
    const pdfBody = message.bodyBin && looksLikeBase64(message.bodyBin) ? message.bodyBin : '';
    const previewSource =
        !canRenderAsSrcDoc && contentTypeCode === 'PDF' && pdfBody
            ? normalizeBase64DataUrl(pdfBody, mediaType)
            : '';
    const previewDoc =
        canRenderAsSrcDoc && textBody
            ? contentTypeCode === 'TEXT'
                ? `<pre style="white-space: pre-wrap; font-family: inherit;">${escapeHtml(
                      textBody,
                  )}</pre>`
                : textBody
            : '';

    return (
        <NotificationsDetailsBody>
            <NotificationsDetailsDate>
                {message.created ? format(new Date(message.created), 'dd.MM.yyyy HH:mm') : EMPTY}
            </NotificationsDetailsDate>

            {attachments.length > 0 && (
                <NotificationsAttachmentList>
                    {attachments.map((content) => (
                        <NotificationsAttachmentItem
                            key={content.id}
                            type="button"
                            onClick={() => onDownloadAttachment(content.id)}
                        >
                            {content.name}
                        </NotificationsAttachmentItem>
                    ))}
                </NotificationsAttachmentList>
            )}

            {previewDoc || previewSource ? (
                <NotificationsPreviewFrame
                    title="notification-preview"
                    src={previewSource || undefined}
                    srcDoc={previewDoc || undefined}
                />
            ) : (
                <NotificationsEmptyMessage>
                    {t('page_notifications.preview-unavailable')}
                </NotificationsEmptyMessage>
            )}
        </NotificationsDetailsBody>
    );
};
